import { motion } from "framer-motion";
import { ArrowUpRight, PackageCheck, Phone, Radar, ScanLine } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const PERKS = [
  { icon: ScanLine, text: "Order & pay in the app — no phone tag" },
  { icon: Radar, text: "Free live tracking on every job" },
  { icon: PackageCheck, text: "Proof of delivery the moment it lands" },
];

export default function OrderApp() {
  return (
    <section id="order" data-testid="order-section" className="relative py-12 sm:py-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#F0A829]/30 bg-gradient-to-br from-[#122A1F] to-[#0C1F17] p-8 sm:p-14">
            <div className="tartan-bg absolute inset-0 opacity-[0.08]" />
            <div className="absolute inset-0 bg-[radial-gradient(700px_320px_at_85%_20%,rgba(240,168,41,0.12),transparent_60%)]" />

            <div className="relative grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <div>
                <Eyebrow>Order direct</Eyebrow>
                <h2 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F6F1E3] sm:text-5xl">
                  Order &amp; pay<br />
                  <span className="text-[#F0A829]">in the app</span>
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-[#B9C4B4]">
                  Book a click &amp; collect run in a couple of taps. Scan the code
                  or head to the site — your parcel, tracked from shelf to doorstep.
                </p>
                <ul className="mt-7 flex flex-col gap-3.5">
                  {PERKS.map((p) => (
                    <li key={p.text} className="flex items-center gap-3.5 text-sm text-[#F6F1E3]/90 sm:text-base">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#F0A829]/40 bg-[#0C1F17]">
                        <p.icon className="h-4 w-4 text-[#F0A829]" strokeWidth={2.2} />
                      </span>
                      {p.text}
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <a
                    data-testid="order-website-button"
                    href="https://kiltedcouriers.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2.5 rounded-full bg-[#F0A829] px-7 py-3.5 font-display text-base font-bold uppercase tracking-wider text-[#0C1F17] transition-transform duration-300 hover:-translate-y-1"
                  >
                    Open the app
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={2.5} />
                  </a>
                  <a
                    data-testid="order-call-button"
                    href="tel:+61408464106"
                    className="flex items-center gap-2.5 rounded-full border border-[#F6F1E3]/25 px-7 py-3.5 font-display text-base font-bold uppercase tracking-wider text-[#F6F1E3] transition-colors duration-300 hover:border-[#F0A829] hover:text-[#F0A829]"
                  >
                    <Phone className="h-4 w-4" strokeWidth={2.5} />
                    0408 464 106
                  </a>
                </div>
              </div>

              <div className="flex justify-center lg:justify-end">
                <motion.div
                  animate={{ y: [0, -10, 0], rotate: [2, 2, 2] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ rotate: 0, scale: 1.03 }}
                  className="relative"
                >
                  <div className="absolute -inset-3 rounded-3xl border border-[#F0A829]/40" />
                  <div className="relative rounded-2xl bg-[#F6F1E3] p-5 shadow-2xl shadow-black/40">
                    <img
                      src="/assets/qr.png"
                      alt="QR code — scan to order at kiltedcouriers.com"
                      data-testid="order-qr-image"
                      className="h-52 w-52 rounded-lg sm:h-60 sm:w-60"
                    />
                    <p className="font-mono-brand mt-4 text-center text-[10px] font-bold uppercase tracking-[0.25em] text-[#0C1F17]">
                      Scan to order in the app
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
